import {
  MULTI_BUILD_COST,
  MULTI_BUILD_REQUIRED_COINS,
  TECH_CARD_DEFINITIONS,
  TECH_COIN_COST,
  UNIT_DEFINITIONS,
} from "../data";
import type {
  BuildMultiAction,
  BuyTechCardAction,
  BuyTechCoinAction,
  EndPhaseAction,
  GameState,
  MoveUnitAction,
  ProduceUnitAction,
  RevealTechCardAction,
  SurrenderAction,
  TriAngerAction,
  ValidationResult,
} from "../types";
import {
  canProduceGrade,
  canProduceStructureAtWorker,
  cellKey,
  countFieldedUnitType,
  distance,
  fieldLimitForUnit,
  findTile,
  getPlayer,
  getPrimaryHomeTile,
  hasOwnedMidTech,
  legalDestinationsForUnit,
  resolveUnitStats,
  samePosition,
} from "../utils";

const VALID: ValidationResult = { valid: true };

function invalid(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function validateActivePlayer(
  state: GameState,
  playerId: string,
): ValidationResult {
  if (state.phase === "GAME_OVER") return invalid("Game is already over.");
  if (state.currentPlayerId !== playerId) {
    return invalid("It is not this player's turn.", {
      currentPlayerId: state.currentPlayerId,
    });
  }
  return VALID;
}

export function validateMoveUnit(
  state: GameState,
  action: MoveUnitAction,
): ValidationResult {
  const active = validateActivePlayer(state, action.playerId);
  if (!active.valid) return active;
  if (state.phase !== "MOVE") return invalid("Units can only move in MOVE.");

  const unit = state.units.find((candidate) => candidate.id === action.unitId);
  if (!unit || unit.ownerId !== action.playerId) {
    return invalid("Unit does not belong to the active player.");
  }

  const tile = findTile(state.board, action.to);
  if (!tile || tile.kind === "blocked") {
    return invalid("Destination is not a movable tile.");
  }

  const stats = resolveUnitStats(state, unit);
  if (stats.mov <= 0) return invalid("This unit cannot move.");
  if (distance(unit.position, action.to) > stats.mov) {
    return invalid("Destination is beyond this unit's movement range.", {
      movement: stats.mov,
    });
  }

  if (
    !legalDestinationsForUnit(state, action.unitId).some((to) =>
      samePosition(to, action.to),
    )
  ) {
    return invalid("Destination is not legal for this unit.");
  }

  return VALID;
}

export function validateProduceUnit(
  state: GameState,
  action: ProduceUnitAction,
): ValidationResult {
  const active = validateActivePlayer(state, action.playerId);
  if (!active.valid) return active;
  if (state.phase !== "PRODUCE") {
    return invalid("Units can only be produced in PRODUCE.");
  }

  const player = getPlayer(state, action.playerId);
  if (!player) return invalid("Player not found.");
  const definition = UNIT_DEFINITIONS[action.unitTypeId];
  if (definition.faction !== player.faction) {
    return invalid("Unit does not belong to the player's faction.");
  }

  const isStructure = definition.structure;
  const isInDeck = player.unitDeck.includes(action.unitTypeId);
  if (!isStructure && !isInDeck) {
    return invalid("Unit is not in the player's selected unit deck.");
  }
  if (
    isStructure &&
    !canProduceStructureAtWorker(state, player.id, action.workerUnitId)
  ) {
    return invalid("Structures require one of your workers on the board.");
  }
  if (!canProduceGrade(player, definition.grade)) {
    return invalid("This unit grade is locked by tech progression.", {
      grade: definition.grade,
    });
  }

  if (
    countFieldedUnitType(state, player.id, action.unitTypeId) >=
    fieldLimitForUnit(action.unitTypeId)
  ) {
    return invalid("Field limit reached for this unit type.");
  }

  if (player.gold < definition.cost) {
    return invalid("Not enough gold to produce this unit.", {
      cost: definition.cost,
      gold: player.gold,
    });
  }

  if (!isStructure) {
    const homeTile = getPrimaryHomeTile(state, player.id);
    if (!homeTile) return invalid("Home tile not found.");
  }

  return VALID;
}

export function validateBuildMulti(
  state: GameState,
  action: BuildMultiAction,
): ValidationResult {
  const active = validateActivePlayer(state, action.playerId);
  if (!active.valid) return active;
  if (state.phase !== "PRODUCE")
    return invalid("M can only be built in PRODUCE.");
  const player = getPlayer(state, action.playerId);
  if (!player) return invalid("Player not found.");
  const tile = findTile(state.board, action.at);
  if (!tile || tile.kind !== "multi")
    return invalid("Target is not an M tile.");
  const hasUnit = state.units.some(
    (unit) =>
      unit.ownerId === action.playerId &&
      samePosition(unit.position, action.at),
  );
  if (!hasUnit) return invalid("You need a unit on the M tile to build it.");
  if (player.gold < MULTI_BUILD_COST)
    return invalid("Not enough gold to build M.");
  const current = state.constructionByCell[cellKey(action.at)] ?? 0;
  if (current >= MULTI_BUILD_REQUIRED_COINS)
    return invalid("M is already active.");
  return VALID;
}

export function validateBuyTechCoin(
  state: GameState,
  action: BuyTechCoinAction,
): ValidationResult {
  const active = validateActivePlayer(state, action.playerId);
  if (!active.valid) return active;
  if (state.phase !== "PRODUCE") {
    return invalid("Tech coins can only be bought in PRODUCE.");
  }
  const player = getPlayer(state, action.playerId);
  if (!player) return invalid("Player not found.");
  if (state.turnFlags.boughtTechCoinPlayerIds.includes(player.id)) {
    return invalid("Only one tech coin can be bought per turn.");
  }
  if (player.gold < TECH_COIN_COST)
    return invalid("Not enough gold for tech coin.");
  return VALID;
}

export function validateBuyTechCard(
  state: GameState,
  action: BuyTechCardAction,
): ValidationResult {
  const active = validateActivePlayer(state, action.playerId);
  if (!active.valid) return active;
  if (state.phase !== "PRODUCE")
    return invalid("Tech cards can only be bought in PRODUCE.");
  const player = getPlayer(state, action.playerId);
  if (!player) return invalid("Player not found.");
  const tech = TECH_CARD_DEFINITIONS[action.techCardId];
  if (tech.faction !== player.faction)
    return invalid("Tech card faction mismatch.");
  if (!player.techDeck.includes(action.techCardId)) {
    return invalid("Tech card is not in the player's selected tech deck.");
  }
  if (player.ownedTech.some((owned) => owned.id === action.techCardId)) {
    return invalid("Tech card already purchased.");
  }
  const cost = tech.grade === "mid" ? 2 : 3;
  if (tech.grade === "adv" && !hasOwnedMidTech(player)) {
    return invalid("Advanced tech requires at least one mid tech.");
  }
  if (player.techCoins < cost) {
    return invalid("Not enough tech coins for this card.", {
      cost,
      techCoins: player.techCoins,
    });
  }
  return VALID;
}

export function validateRevealTechCard(
  state: GameState,
  action: RevealTechCardAction,
): ValidationResult {
  const active = validateActivePlayer(state, action.playerId);
  if (!active.valid) return active;
  const player = getPlayer(state, action.playerId);
  if (!player) return invalid("Player not found.");
  const owned = player.ownedTech.find((tech) => tech.id === action.techCardId);
  if (!owned) return invalid("Tech card has not been purchased.");
  if (owned.revealed) return invalid("Tech card already revealed.");
  if (owned.purchasedTurn === state.turn) {
    return invalid(
      "Tech card cannot be revealed on the turn it was purchased.",
    );
  }
  if (action.techCardId === "SB_GOLDEN_TEMPLE") {
    if (!action.target) return invalid("Golden Temple requires an M target.");
    const tile = findTile(state.board, action.target);
    if (!tile || tile.kind !== "multi")
      return invalid("Target is not an M tile.");
    const hasUnit = state.units.some(
      (unit) =>
        unit.ownerId === player.id &&
        samePosition(unit.position, action.target ?? { x: -1, y: -1 }),
    );
    if (!hasUnit) {
      return invalid("Golden Temple requires your unit on the target M tile.");
    }
  }
  return VALID;
}

export function validateEndPhase(
  state: GameState,
  action: EndPhaseAction,
): ValidationResult {
  return validateActivePlayer(state, action.playerId);
}

export function validateSurrender(
  state: GameState,
  action: SurrenderAction,
): ValidationResult {
  if (state.phase === "GAME_OVER") return invalid("Game is already over.");
  if (!getPlayer(state, action.playerId)) return invalid("Player not found.");
  return VALID;
}

export function validateAction(
  state: GameState,
  action: TriAngerAction,
): ValidationResult {
  switch (action.type) {
    case "MoveUnit":
      return validateMoveUnit(state, action);
    case "ProduceUnit":
      return validateProduceUnit(state, action);
    case "BuildMulti":
      return validateBuildMulti(state, action);
    case "BuyTechCoin":
      return validateBuyTechCoin(state, action);
    case "BuyTechCard":
      return validateBuyTechCard(state, action);
    case "RevealTechCard":
      return validateRevealTechCard(state, action);
    case "EndPhase":
      return validateEndPhase(state, action);
    case "Surrender":
      return validateSurrender(state, action);
  }
}
